/**
 * Typed swap API helpers.
 *
 * All swap calls go to the swap service under /v1/swap, reached through the
 * configured swap base (see config.swapApiBaseUrl and swapUrl). With the default
 * empty base this is the current origin, which the reverse proxy routes to the
 * swap service; in local development the Vite dev server rewrites /v1/swap to a
 * locally running swap service (see vite.config.ts). The frontend contract is
 * the same either way. Every call in this module passes `resolveUrl: swapUrl` so
 * swap traffic targets the swap base rather than the gateway base.
 *
 * A quote fan-out where every provider fails returns 422 with the same
 * AggregatedSwapQuotes body (per-provider errors inline). That is a displayable
 * outcome, not a transport failure, so getSwapQuotes catches that specific case
 * and returns the body; callers render the per-provider errors.
 */

import { request, ApiError, type SignalOptions } from './api';
import { swapUrl } from '@/config';
import type {
  AggregatedSwapQuotes,
  SwapChain,
  SwapQuoteRequestBody,
  SwapToken,
  TrackedSwap,
} from './swap';

/**
 * Fetch supported chains per provider.
 *
 * @param opts Options.
 * @returns Map of provider name to that provider's normalized chain list.
 */
export async function getSwapChains(
  opts: SignalOptions = {},
): Promise<Record<string, SwapChain[]>> {
  const res = await request<{ chains: Record<string, SwapChain[]> }>('/v1/swap/cross-chain/chains', {
    method: 'GET',
    resolveUrl: swapUrl,
    swapIdentity: true,
    signal: opts.signal,
  });
  return res.chains ?? {};
}

/**
 * Fetch the unified token list for one chain.
 *
 * @param chainId Standard chain id.
 * @param opts Options.
 * @returns Deduplicated tokens, majors first (sorted by provider coverage).
 */
export async function getSwapTokens(chainId: number, opts: SignalOptions = {}): Promise<SwapToken[]> {
  const res = await request<{ tokens: SwapToken[] }>(
    `/v1/swap/cross-chain/tokens/${encodeURIComponent(String(chainId))}`,
    { method: 'GET', resolveUrl: swapUrl, swapIdentity: true, signal: opts.signal },
  );
  return res.tokens ?? [];
}

/**
 * Fetch quotes from all providers for one swap. Routes to the same-chain or
 * cross-chain endpoint from the chain ids, mirroring the aggregator's split
 * (each side rejects the other's requests with a redirect hint).
 *
 * A 422 with a quotes body means "well-formed, but no provider can fill it";
 * the body still carries each provider's error, so it is returned for display
 * rather than thrown. Any other failure propagates as an ApiError.
 *
 * @param body The quote request.
 * @param opts Options.
 * @returns The aggregated quotes, possibly with zero successful entries.
 */
export async function getSwapQuotes(
  body: SwapQuoteRequestBody,
  opts: SignalOptions = {},
): Promise<AggregatedSwapQuotes> {
  const path =
    body.originChainId === body.destinationChainId
      ? '/v1/swap/same-chain/quotes'
      : '/v1/swap/cross-chain/quotes';
  try {
    return await request<AggregatedSwapQuotes>(path, {
      method: 'POST',
      body,
      // A quote is a read shaped as a POST; retrying is safe but the fan-out is
      // slow, so a failed attempt surfaces immediately instead of retrying.
      retry: false,
      resolveUrl: swapUrl,
      swapIdentity: true,
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 422 && isQuotesBody(err.body)) {
      return err.body;
    }
    throw err;
  }
}

/**
 * One swap's status as reported by the swap service through the aggregator.
 *
 * The service already normalizes `status` to its closed vocabulary and folds
 * provider detail into `subStatus`, so the client switches on these rather than
 * re-parsing raw provider strings. `subStatus` carries the provider's refinement
 * (for example "waiting", "delayed", "refund", "slowfill") that the tracker uses
 * to distinguish a refund from a failure and to corroborate a taking-longer
 * signal. `amountOut` is populated live by Across from the destination fill; for
 * Relay it is absent, so the tracker falls back to the quoted expected output it
 * captured at execute time. All fields past `status` are optional because a
 * given provider or a still-early poll may not carry them yet.
 */
export interface SwapStatus {
  provider: string;
  requestId: string;
  status: string;
  /** Provider-specific refinement of `status` for display, never re-keyed. */
  subStatus?: string;
  /** Origin deposit transaction hash, once observed. */
  srcTxHash?: string;
  /** Destination fill transaction hash: the deliver-to-user signal. */
  dstTxHash?: string;
  /** Origin input amount in smallest units, when the provider echoes it. */
  amountIn?: string;
  /** Delivered output amount in smallest units (Across live; Relay absent). */
  amountOut?: string;
  /** A provider or transport error string when the poll itself failed. */
  error?: string;
}

/**
 * Poll one swap's status. This call has a server-side side effect by design:
 * the swap service updates the tracker row (status, tx hashes) and, on
 * completion, captures the realized fee — so polling from the UI is what
 * advances a swap from "submitted" to "completed" in the tracker. Same-chain
 * and cross-chain share the status route shape; cross-chain is used since the
 * tracker keys are provider-scoped, not route-scoped.
 *
 * @param provider The provider name (relay, across).
 * @param requestId The id returned at execute time (Across: deposit tx hash).
 * @param opts Options.
 * @returns The current status.
 */
export function getSwapStatus(
  provider: string,
  requestId: string,
  opts: SignalOptions = {},
): Promise<SwapStatus> {
  return request<SwapStatus>(
    `/v1/swap/cross-chain/status/${encodeURIComponent(provider)}/${encodeURIComponent(requestId)}`,
    { method: 'GET', resolveUrl: swapUrl, swapIdentity: true, signal: opts.signal },
  );
}

/**
 * The swap service's live fee reading for one specific call.
 *
 * Every value is a decimal wei string, not a number: these are wei-scale
 * integers and a JSON number is a float in most parsers, which would silently
 * round a fee.
 */
export interface SwapGasEstimate {
  chain_id: number;
  /** The origin chain's current base fee per gas. */
  base_fee_per_gas: string;
  /** The node's suggested priority fee, floored by the service. */
  max_priority_fee_per_gas: string;
  /** What eth_estimateGas returned for this exact call, unpadded. */
  estimated_gas: string;
  /** base fee x 2 + tip: the ceiling to sign with. */
  suggested_max_fee_per_gas: string;
  /** The estimate plus headroom: the limit to sign with. */
  suggested_gas_limit: string;
}

/** The call to price. Must be the transaction that will actually be signed. */
export interface SwapGasRequest {
  chain_id: number;
  from: string;
  to: string;
  data: string;
  /** Decimal wei. */
  value: string;
}

/**
 * Read live fee data for a call from the chain that will execute it.
 *
 * This replaces the hardcoded fee constants this module's caller used to fall
 * back to. A browser is held to connect-src 'self' and cannot reach a JSON-RPC,
 * so the swap service — which can — opens the connection, reads the base fee, a
 * suggested tip and an eth_estimateGas for this exact call, and answers.
 *
 * A rejection is meaningful rather than incidental: eth_estimateGas executes the
 * call against live state, so a route whose calldata would revert fails here,
 * before anything is signed.
 *
 * @param body The call to price.
 * @param opts Options.
 * @returns The live readings and the pair to sign with.
 */
export function getSwapGas(
  body: SwapGasRequest,
  opts: SignalOptions = {},
): Promise<SwapGasEstimate> {
  return request<SwapGasEstimate>('/v1/swap/cross-chain/gas', {
    method: 'POST',
    body,
    resolveUrl: swapUrl,
    swapIdentity: true,
    retry: false,
    signal: opts.signal,
  });
}

/** The sender account nonce as reported by the gateway for a wallet on a chain. */
export interface SwapNonce {
  /** The EVM chain id the nonce was read for, echoed by the gateway. */
  chainId: number;
  /** The wallet's own EVM address the nonce was read for, lowercase 0x hex.
   * Derived server-side from the wallet's key, never supplied by the client. */
  address: string;
  /** The next account nonce as a base-10 integer string. */
  nonce: string;
}

/** The raw gateway nonce response before it is normalized to {@link SwapNonce}. */
interface GatewayNonceResponse {
  chain_id: number;
  address: string;
  nonce: string;
}

/**
 * Read the origin-chain sender nonce for building an EVM swap envelope.
 *
 * The browser cannot read a public JSON-RPC directly (the app's
 * Content-Security-Policy is connect-src 'self'), and the gateway signs the
 * envelope a caller supplies rather than filling the nonce itself. The gateway
 * already holds a per-chain EVM RPC client (it broadcasts and reads balances),
 * so the nonce is read from the gateway over the existing same-origin api layer,
 * not from the swap service, which is a pure quote aggregator with no chain RPC.
 * The gateway resolves the wallet's EVM address itself from the stored key and
 * scopes the read to the caller's own wallet, so no address is supplied here and
 * a wallet in another org answers 404.
 *
 * When the gateway does not expose the nonce endpoint yet (404), this resolves
 * to null so the caller can fail closed with a clear "execution not yet
 * available" message rather than signing an envelope with a guessed nonce that
 * would either revert or replay. Any other failure propagates so the swap does
 * not sign against an unread nonce.
 *
 * @param walletId The origin wallet whose account nonce is read.
 * @param chain The gateway registry chain key for the origin, e.g. "sepolia".
 * @param opts Options.
 * @returns The nonce record, or null when the endpoint is not available.
 */
export async function getSwapNonce(
  walletId: string,
  chain: string,
  opts: SignalOptions = {},
): Promise<SwapNonce | null> {
  try {
    const res = await request<GatewayNonceResponse>(
      `/v1/wallets/${encodeURIComponent(walletId)}/evm-nonce`,
      {
        method: 'GET',
        // Resolved against the gateway base (the default), which the CSP allows
        // as same-origin. No swapUrl override: this is a gateway read, not a
        // swap-service call.
        query: { chain },
        // A read, so a transient failure may retry; the nonce is idempotent.
        retry: true,
        // Never cache a nonce: a stale value would build a replay or a gap.
        cache: 'no-store',
        signal: opts.signal,
      },
    );
    return {
      chainId: res.chain_id,
      address: res.address,
      nonce: res.nonce,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Fetch the newest tracked swaps from the swap tracker. A deployment without
 * tracking enabled answers 404; that is "feature off", not an error, so it
 * resolves to an empty list and the Activity section simply doesn't render.
 *
 * @param limit Maximum rows to return.
 * @param opts Options.
 * @returns Tracked swaps, newest first; empty when tracking is off.
 */
export async function getTrackedSwaps(
  limit = 20,
  user?: string,
  opts: SignalOptions = {},
): Promise<TrackedSwap[]> {
  try {
    const res = await request<{ swaps: TrackedSwap[] }>('/v1/swap/swaps', {
      method: 'GET',
      resolveUrl: swapUrl,
      swapIdentity: true,
      // The server scopes to `user` when provided, so other wallets' swaps
      // are never sent to this client.
      query: { limit, user },
      signal: opts.signal,
    });
    return res.swaps ?? [];
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}


/* -------------------------------------------------------------------------- *
 * Origin tracking: the two calls that bracket a swap this wallet signs itself.
 *
 * WHY THEY EXIST. A swap succeeded on staging and the tracker held zero rows.
 * This app never calls /execute -- it builds its own envelope, has it signed and
 * broadcast, then polls status -- and /execute was the only path that ever wrote
 * a row. Nothing recorded the swap because nothing was asked to.
 *
 * The ordering is the whole design: PREPARE BEFORE ANYTHING IRREVERSIBLE. A
 * refused start is recoverable in seconds; an unrecorded success is not.
 * -------------------------------------------------------------------------- */

/**
 * What the client commits to before signing.
 *
 * Every field here is part of the binding the server later checks a broadcast
 * transaction against. `from`, `nonce`, `to`, `value` and `data` together are
 * what separate this swap from the same wallet's NEXT swap through the same
 * router -- chain and addresses alone are not enough.
 */
export interface SwapOriginIntent {
  provider: string;
  /** Idempotency key, scoped to the org and provider by the server. */
  request_id: string;
  chain_id: number;
  /** The sender, as the gateway derived it. Never a client-chosen address. */
  from: string;
  nonce: number;
  to: string;
  /** Decimal wei. */
  value: string;
  /** 0x-prefixed calldata; "0x" for a plain transfer. */
  data: string;
}

/** The opaque handle the server issues for a prepared swap. */
export interface SwapOriginPrepared {
  tracking_id: string;
}

/**
 * Record a swap BEFORE it is signed, and receive its tracking id.
 *
 * A FAILURE HERE MUST STOP THE SWAP. The caller is expected to abandon rather
 * than broadcast. That is a product policy for an accounting-critical wallet,
 * not a universal law: it trades a small amount of availability for never having
 * a swap that moved funds and left no record.
 *
 * Not retried. A retry would race the user's own decision to abandon, and the
 * server's idempotency makes a deliberate re-attempt safe anyway.
 *
 * @param intent The commitment being made.
 * @param opts Options.
 * @returns The tracking id to report the broadcast hash against.
 */
export function prepareSwapOrigin(
  intent: SwapOriginIntent,
  opts: SignalOptions = {},
): Promise<SwapOriginPrepared> {
  return request<SwapOriginPrepared>('/v1/swap/swaps', {
    method: 'POST',
    body: intent,
    resolveUrl: swapUrl,
    swapIdentity: true,
    retry: false,
    signal: opts.signal,
  });
}

/**
 * Report the transaction hash a prepared swap was broadcast as.
 *
 * BEST EFFORT, AND DELIBERATELY SO. The swap has already happened by the time
 * this runs; failing the user's flow here would help nobody. A hash that never
 * arrives leaves the row 'unreported', which is an honest state and NOT a claim
 * that nothing was broadcast -- the server never treats it as one.
 *
 * The hash is a CANDIDATE, not a fact: the server verifies it against the
 * commitments made at prepare time before it becomes the swap's transaction.
 *
 * @param trackingId The handle from {@link prepareSwapOrigin}.
 * @param txHash The 0x-prefixed 32-byte broadcast hash.
 * @param opts Options.
 * @returns Nothing; failures are swallowed by the caller by design.
 */
export function reportSwapOriginTx(
  trackingId: string,
  txHash: string,
  opts: SignalOptions = {},
): Promise<unknown> {
  return request<unknown>(`/v1/swap/swaps/${encodeURIComponent(trackingId)}/tx`, {
    method: 'POST',
    body: { tx_hash: txHash },
    resolveUrl: swapUrl,
    swapIdentity: true,
    retry: false,
    signal: opts.signal,
  });
}

/**
 * The server's view of one swap on the ORIGIN axis: what the chain says.
 *
 * Distinct from {@link TrackedSwap}, which is the PROVIDER axis (what Relay or
 * Across says became of the bridge request). The two are independent and the UI
 * must never infer one from the other -- a provider reporting nothing yet does
 * not mean the origin transaction failed, and vice versa.
 */
export interface SwapOriginRecord {
  tracking_id: string;
  provider: string;
  request_id: string;
  chain_id: number;
  /** unreported | reported | verified | reverted | unknown. */
  origin_status: string;
  /** Present ONLY when verified. A merely reported hash is never disclosed. */
  origin_tx_hash?: string;
  origin_block_number?: number;
  origin_verified_at?: string;
  /** The provider settlement axis, unchanged. */
  provider_status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Restore this org's recent prepared swaps from the server.
 *
 * THE CLIENT CANNOT BE THE MEMORY. A swap survives a closed tab, a crashed
 * renderer and a new device; localStorage survives none of them. This is how a
 * client that lost its state learns what is actually outstanding.
 *
 * A deployment without tracking answers 404. That is "feature off", not an
 * error, and resolves to an empty list.
 *
 * @param opts Options.
 * @returns Prepared swaps, newest first; empty when tracking is off.
 */
export async function getSwapOrigins(
  opts: SignalOptions = {},
): Promise<SwapOriginRecord[]> {
  try {
    const res = await request<{ swaps: SwapOriginRecord[] }>('/v1/swap/swaps/origins', {
      method: 'GET',
      resolveUrl: swapUrl,
      swapIdentity: true,
      signal: opts.signal,
    });
    return res.swaps ?? [];
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}

/** Narrow an unknown error body to the aggregated-quotes shape. */
function isQuotesBody(body: unknown): body is AggregatedSwapQuotes {
  return (
    typeof body === 'object' &&
    body !== null &&
    Array.isArray((body as { quotes?: unknown }).quotes)
  );
}
